import { FixedRichTextToolbar } from "@/common/editor/RuleAuthor/toolbar/FixedRichTextToolbar";
import { PlateRichTextEditor, richTextPlugins } from "@/common/editor/components/PlateRichTextEditor";
import { InlineEdit } from "@/components/InlineEdit";
import { UserCombobox } from "@/components/radix/UserSelect/UserSelect";
import { AssigneeGroup } from "@/ui/forms/assignee-group";
import styled from "@emotion/styled";
import Button from "@imminently/decisively-ui/Button";
import CheckboxField from "@imminently/decisively-ui/CheckboxField";
import DateTimeField from "@imminently/decisively-ui/DateTimeField";
import { Controller, useFormContext, useFormState } from "@imminently/decisively-ui/Form";
import Icon from "@imminently/decisively-ui/Icon";
import InputBox from "@imminently/decisively-ui/InputBox";
import MultiSelectField from "@imminently/decisively-ui/MultiSelectField";
import SelectField from "@imminently/decisively-ui/SelectField";
import TextField, { type TextFieldProps } from "@imminently/decisively-ui/TextField";
import { Typography } from "@mui/material";
import { PlateProvider } from "@udecode/plate";
import clsx from "clsx";
import type { PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PriorityLabel, StatusLabel } from "./TaskComponents";
import { editableProps, priorityOptions, statusOptions } from "./taskUtils";
import styles from "./tasks.module.scss";

export const TaskDetail = ({ label, icon, children }: { label: string; icon?: ReactNode } & PropsWithChildren) => {
  return (
    <div className={styles.detail}>
      <div
        style={{ flex: 2 }}
        className={styles.detail}
      >
        {icon}
        <Typography
          aria-label={label}
          style={{ flexShrink: 0 }}
        >
          {label}
        </Typography>
      </div>
      <div
        aria-labelledby={label}
        style={{ display: "flex", flex: 3 }}
      >
        {children}
      </div>
    </div>
  );
};

export const TitleField = () => {
  const { control } = useFormContext();
  const formState = useFormState({ control, name: "title" });
  const { t } = useTranslation();
  const hasError = Boolean(formState?.errors?.title);

  return (
    <Controller
      name="title"
      control={control}
      rules={{
        required: true,
      }}
      render={({ field }) => (
        <Typography
          variant="h3"
          style={{ lineHeight: "2rem", border: hasError ? "2px solid red" : "" }}
        >
          <InlineEdit
            className={styles.title}
            placeholder={`${t("tasks.title_placeholder")}...*`}
            value={field.value}
            onChange={field.onChange}
          />
        </Typography>
      )}
    />
  );
};

const StyledStatusOption = styled.div`
  .MuiChip-root {
    height: 28px;
    font-size: 0.875rem;

    .MuiChip-label {
      padding: 0 12px;
    }
  }
`;

const formatStatusOption = (option: any) => (
  <StyledStatusOption>
    <StatusLabel status={option.value} />
  </StyledStatusOption>
);

const formatPriorityOption = (option: any) => <PriorityLabel priority={option.value} />;

const statusSelectOptions = statusOptions.map((option) => ({
  ...option,
  label: option.label,
}));

export const StatusSelector = ({ omit = [] }: { omit?: string[] | ((val: string) => boolean) }) => {
  const { t } = useTranslation();
  const items = statusSelectOptions.filter((val) =>
    Array.isArray(omit) ? !omit.includes(val.value) : omit(val.value),
  );

  return (
    <SelectField
      name="status"
      options={items}
      placeholder={`${t("tasks.status_placeholder")}...`}
      formatOptionLabel={formatStatusOption}
      fullWidth
    />
  );
};

export const AssigneeSelector = () => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="assigneesData"
      control={control}
      render={({ field }) => (
        <>
          <UserCombobox
            value={field.value}
            onChange={(value) => field.onChange(value)}
            searchProps={{ placeholder: t("search") }}
          >
            <InputBox fullWidth>
              <AssigneeGroup
                users={field.value}
                max={4}
              />
            </InputBox>
          </UserCombobox>
        </>
      )}
    />
  );
};

export const DateSelector = ({ label }: { label: string }) => {
  return (
    <DateTimeField
      name="dueDate"
      dateOnly
      placeholder={label}
      fullWidth
    />
  );
};

const prioritySelectOptions = priorityOptions.map((option) => ({
  ...option,
  label: option.label,
}));

export const PrioritySelector = () => {
  const { t } = useTranslation();
  return (
    <SelectField
      name="priority"
      options={prioritySelectOptions}
      placeholder={`${t("tasks.priority_placeholder")}...`}
      formatOptionLabel={formatPriorityOption}
      fullWidth
    />
  );
};

export const TagsInput = () => {
  return (
    <MultiSelectField
      name="tags"
      allowArbitrary={true}
      options={[]}
      fullWidth
    />
  );
};

export const DescriptionInput = () => {
  const { control } = useFormContext();
  const { t } = useTranslation();

  return (
    <Controller
      name="description"
      control={control}
      render={({ field }) => (
        <InputBox
          className={clsx(styles.textBox, styles.description)}
          fullWidth
        >
          <PlateProvider
            id={"description-editor"}
            plugins={richTextPlugins}
            value={field.value}
            onChange={field.onChange}
          >
            <FixedRichTextToolbar className={styles.toolbar} />
            <PlateRichTextEditor
              id={"description-editor"}
              editableProps={editableProps({ placeholder: `${t("tasks.description_placeholder")}...` })}
            />
          </PlateProvider>
        </InputBox>
      )}
    />
  );
};

const TextAreaInput = ({ error: _error, ...props }: any) => <textarea {...props} />;

type TaskTextInputProps = TextFieldProps & {
  fullWidth?: boolean;
  multiline?: boolean;
  minRows?: number;
};

export const TextInput = ({ fullWidth: _fullWidth, multiline, minRows, rows, ...props }: TaskTextInputProps) => {
  return (
    <TextField
      {...props}
      inputComponent={multiline ? TextAreaInput : props.inputComponent}
      rows={rows ?? minRows}
      fullWidth
    />
  );
};

export const CheckboxInput = ({ name, label }: { name: string; label: string }) => {
  return (
    <CheckboxField
      name={name}
      label={label}
    />
  );
};
